import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase, getAppUserId, getMembershipStatus } from './lib/supabase';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MapPage from './pages/MapPage';
import CheckInPage from './pages/CheckInPage';
import JoinPage from './pages/JoinPage';
import WelcomePage from './pages/WelcomePage';
import AdminPage from './pages/AdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrintMapPage from './pages/PrintMapPage';
import { isAdmin } from './lib/admin';
import Navigation from './components/Navigation';
import AppHeader from './components/AppHeader';
import './styles/App.css';

type Membership = 'checking' | 'active' | 'lapsed' | 'none';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const [membership, setMembership] = useState<Membership>('checking');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user || null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.email) {
      setAppUserId(null);
      setMembership('checking');
      return;
    }
    getAppUserId(user.email).then(async (id) => {
      setAppUserId(id);
      setMembership(id ? await getMembershipStatus(id) : 'none');
    });
  }, [user]);

  const activated = useCallback(() => setMembership('active'), []);

  if (loading || (user && membership === 'checking')) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {!user ? (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : membership === 'none' ? (
        <Routes>
          <Route path="/reset" element={<ResetPasswordPage />} />
          <Route
            path="/welcome"
            element={<WelcomePage appUserId={appUserId} onActivated={activated} />}
          />
          <Route
            path="/join"
            element={<JoinPage email={user.email || ''} appUserId={appUserId} />}
          />
          <Route path="*" element={<Navigate to="/join" replace />} />
        </Routes>
      ) : (
        <div className="app-shell">
          <AppHeader />
          <Routes>
            <Route
              path="/"
              element={<DashboardPage appUserId={appUserId} readOnly={membership === 'lapsed'} />}
            />
            <Route path="/map" element={<MapPage appUserId={appUserId} />} />
            <Route path="/print-map" element={<PrintMapPage appUserId={appUserId} />} />
            <Route
              path="/checkin"
              element={<CheckInPage appUserId={appUserId} readOnly={membership === 'lapsed'} />}
            />
            {membership === 'lapsed' && (
              <Route
                path="/join"
                element={<JoinPage email={user.email || ''} appUserId={appUserId} />}
              />
            )}
            {membership === 'lapsed' && (
              <Route
                path="/welcome"
                element={<WelcomePage appUserId={appUserId} onActivated={activated} />}
              />
            )}
            {isAdmin(user.email) && <Route path="/admin" element={<AdminPage />} />}
            <Route path="/reset" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Navigation admin={isAdmin(user.email)} lapsed={membership === 'lapsed'} />
        </div>
      )}
    </BrowserRouter>
  );
}

export default App;
